import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../../components/common/BackButton";
import { Button } from "../../components/common/Button";
import { PageTitle } from "../../components/common/PageData";
import { supabaseClient } from "../../shared/supabase";
import { useCourseStore } from "../../stores/course.store";
import { useSeasonStore } from "../../stores/season.store";
import { useUserStore, type CourseSubscription } from "../../stores/user.store";
import type { TemporarySubscription } from "../../types/course";
import * as SentryUtils from "../../utils/sentry";
import { CoursePendingRecap } from "./CoursePendingRecap";
import { CoursePaymentNotice } from "./CoursePaymentNotice";
import { useCourseSubscriptionContext } from "./useCourseSubscriptionContext";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const MemberList = styled.ul`
  list-style: none;
  padding: 0;
  border: 1px solid black;
`;

const MemberItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  &:last-child { border-bottom: 0; }
`;

const Total = styled.div`
  background: #e6f3ff;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
`;

export const CourseCheckout = () => {
  const navigate = useNavigate();
  const { profile, loadProfile } = useUserStore();
  const { currentSeason } = useSeasonStore();
  const { currentCourse } = useCourseStore();
  const { tempSubscriptions, setTempSubscriptions } = useCourseSubscriptionContext();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CourseSubscription[] | null>(null);
  const [uncertain, setUncertain] = useState(false);

  const registrationsOpen = currentSeason && currentCourse?.season_id === currentSeason.id &&
    Date.now() >= new Date(currentCourse.subscription_start_date).getTime() &&
    Date.now() <= new Date(currentCourse.subscription_end_date).getTime();
  const selections = Array.from(tempSubscriptions.values()).filter((subscription) => {
    const member = profile?.members.find((item) => item.id === subscription.subscriber_id);
    return subscription.course_id === currentCourse?.id && member &&
      !member.course_subscriptions.some((existing) => existing.course_id === currentCourse?.id);
  });

  const matchesRequestedRow = (row: CourseSubscription, requested: TemporarySubscription) =>
    typeof row.id === "string" && row.id.length > 0 &&
    row.course_id === requested.course_id &&
    row.subscriber_id === requested.subscriber_id &&
    row.type === requested.type &&
    row.level === requested.level &&
    row.companion_id === requested.companion_id &&
    row.status === "pending";

  const matchesRequest = (rows: CourseSubscription[], requested: TemporarySubscription[]) =>
    requested.every((selection) => rows.some((row) => matchesRequestedRow(row, selection)));

  const pendingSubscriptions = useMemo(() => {
    const pending = (profile?.members ?? []).flatMap((member) =>
      member.course_subscriptions.filter((row) => row.course_id === currentCourse?.id && row.status === "pending")
    );
    result?.forEach((row) => {
      if (row.status === "pending" && !pending.some((item) => item.id === row.id)) {
        pending.push(row);
      }
    });
    return pending;
  }, [result, profile, currentCourse]);

  const onReserve = async () => {
    if (isLoading || uncertain || result || !currentCourse || !registrationsOpen || selections.length === 0) {
      return;
    }
    setIsLoading(true);
    let saved: CourseSubscription[] | null = null;
    try {
      const { error, data } = await supabaseClient.functions.invoke("server/reserve_courses", {
        method: "POST",
        body: { courseId: currentCourse.id, subscriptions: selections },
      });
      if (error) {
        throw error;
      }

      // Use the persisted rows from the response immediately: loadProfile swallows
      // query errors and may retain a stale profile after a successful write.
      if (data?.courseId === currentCourse.id &&
        typeof data.emailSent === "boolean" &&
        Array.isArray(data?.subscriptions) &&
        data.subscriptions.length === selections.length &&
        matchesRequest(data.subscriptions as CourseSubscription[], selections)) {
        saved = data.subscriptions as CourseSubscription[];
      }
    } catch (error) {
      SentryUtils.captureException(error, {
        tags: { component: "CourseCheckout", operation: "reserve_courses" },
        extra: { courseId: currentCourse.id },
      });
    }


    if (saved) {
      setResult(saved);
      setTempSubscriptions(new Map());
      // Refresh in the background for the next visit, but keep the validated
      // response recap visible even if profile loading fails or is stale.
      try {
        await loadProfile();
      } catch (error) {
        console.error("Could not refresh course reservations", error);
      }
    } else {
      setUncertain(true);
    }
    setIsLoading(false);
  };

  const returnToCourses = async () => {
    if (result) {
      navigate("/account/courses");
      return;
    } else if (uncertain) {
      if (!currentCourse) {
        return;
      }
      setIsLoading(true);
      const requestedIds = Array.from(tempSubscriptions.values())
        .filter((selection) => selection.course_id === currentCourse.id)
        .map((selection) => selection.subscriber_id);
      // No read can prove that an ambiguous POST did not commit after it.
      // Discard the submitted drafts before leaving so remounting checkout
      // never exposes an automatic retry, even if the profile is stale.
      setTempSubscriptions((previous) => {
        const next = new Map(previous);
        requestedIds.forEach((id) => next.delete(id));
        return next;
      });
      setIsLoading(false);
    }
    navigate("/account/courses");
  };

  if (!currentSeason || !currentCourse || !profile || (!registrationsOpen && !result && !uncertain)) {
    return (
      <Container>
        <BackButton onClick={() => navigate("/account/courses")} />
        <PageTitle>ISCRIZIONE CORSO</PageTitle>
        <p>Le nuove iscrizioni ai corsi sono temporaneamente chiuse.</p>
        <Button fullWidth onClick={() => navigate("/account/courses")}>TORNA AI CORSI</Button>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={returnToCourses} />
      <PageTitle>{result ? "ISCRIZIONE INVIATA" : "RIEPILOGO ISCRIZIONE"}</PageTitle>
      {result ? (
        <>
          <CoursePendingRecap members={profile.members} subscriptions={pendingSubscriptions} season={currentSeason} />
          {result.every((row) => row.status === "confirmed") && <p>Tutti i membri risultano confermati.</p>}
        </>
      ) : uncertain ? (
        <p role="alert">Non sappiamo se l'iscrizione è stata inviata. Torna ai corsi e controlla lo stato prima di riprovare.</p>
      ) : selections.length === 0 ? (
        <p>Nessun membro da iscrivere. Torna ai corsi per scegliere i partecipanti.</p>
      ) : (
        <>
          <CoursePaymentNotice />
          <p>Controlla i membri da iscrivere prima dell'invio.</p>
          <MemberList>
            {selections.map((item) => {
              const member = profile.members.find((m) => m.id === item.subscriber_id);
              return (
                <MemberItem key={item.subscriber_id}>
                  <span><strong>{member?.first_name} {member?.last_name}</strong><br />
                    {item.type === "ski" ? "SCI" : "SNOWBOARD"} · LIVELLO {item.level}</span>
                  <span>{currentCourse.price.toFixed(2)} €</span>
                </MemberItem>
              );
            })}
          </MemberList>
          <Total><span>TOTALE</span><span>{(selections.length * currentCourse.price).toFixed(2)} €</span></Total>
          <Button fullWidth onClick={onReserve} loading={isLoading} loadingText="INVIO IN CORSO..." disabled={isLoading}>
            INVIA ISCRIZIONE
          </Button>
        </>
      )}
      {(result || uncertain || selections.length === 0) && (
        <Button fullWidth variant="outline" loading={isLoading} onClick={returnToCourses}>
          {result ? "TORNA AI CORSI" : "VERIFICA ISCRIZIONI NEI CORSI"}
        </Button>
      )}
    </Container>
  );
};
